import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Brain,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  Search,
  Hexagon,
} from "lucide-react";
import { DesktopLayout } from "@/layouts";
import { ensureUsdtSymbol, removeUsdtSuffix } from "@/utils/symbols";
import {
  usePreviousAlerts,
  useLeaderboardAlerts,
  useIncomingListings,
} from "@/hooks/useCryptoAlerts";
import { useLeaderboard } from "@/hooks/useEventLeaderboard";
import { useTokenMarket, useTokenMetadata } from "@/hooks/useTokenMetadata";
import { metadataTags } from "@/api/tokenMetadata";
import { TokenMarketStats } from "@/components/analyze/TokenMarketStats";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { LEADERBOARD_ROW_GRID, LeaderboardTokenTile } from "@/components/LeaderboardTokenTile";
import { INCOMING_ROW_GRID, IncomingListingTile } from "@/components/IncomingListingTile";
import { AnalyzeHero, TableCard, TableHead, ViewAllButton } from "@/components/analyze/AnalyzeSections";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { exchangeBrand } from "@/lib/exchange-brand";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertHistory } from "@/components/AlertHistory";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { PositionTradeDrawerDesktop } from "@/pages/alerts/drawers/PositionTradeDrawer.Desktop";

const LEADERBOARD_DISPLAY_COUNT = 10;
/** Rows per table before "View all". */
const COLLAPSED_ROWS = 5;

/**
 * Desktop version of the Analyze page
 * - Dashboard: Leaderboards (top gainers/losers) + Incoming listings + Search
 * - Analysis: Full token analysis with back button
 */
export const AnalyzePageDesktop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const symbolFromUrl = searchParams.get("symbol");
  const [inputSymbol, setInputSymbol] = useState("");
  const [analyzedSymbol, setAnalyzedSymbol] = useState<string | null>(
    symbolFromUrl
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);

  // Sync URL with analyzed symbol
  useEffect(() => {
    if (symbolFromUrl !== analyzedSymbol) {
      setAnalyzedSymbol(symbolFromUrl);
    }
  }, [symbolFromUrl]);

  const handleAnalyzeSymbol = useCallback(
    (symbol: string) => {
      const normalized = ensureUsdtSymbol(symbol.trim().toUpperCase());
      setAnalyzedSymbol(normalized);
      setSearchParams({ symbol: removeUsdtSuffix(normalized) });
    },
    [setSearchParams]
  );

  const handleBackToDashboard = useCallback(() => {
    setAnalyzedSymbol(null);
    setInputSymbol("");
    setSearchParams({});
  }, [setSearchParams]);

  // Normalize symbol for API calls
  const normalizedSymbol = useMemo(
    () => (analyzedSymbol ? ensureUsdtSymbol(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const baseSymbol = useMemo(
    () => (analyzedSymbol ? removeUsdtSuffix(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const isAnalysisView = !!analyzedSymbol;

  // Dashboard data
  const { data: leaderboardData, isLoading: isLoadingLeaderboard } =
    useLeaderboardAlerts(!isAnalysisView);
  const { data: incomingListings = [], isLoading: isLoadingIncoming } =
    useIncomingListings(!isAnalysisView);

  const {
    sortedGainers,
    sortedLosers,
    pricedGainers,
    pricedLosers,
    isPriceLoading,
    handlePriceUpdate,
  } = useLeaderboard(leaderboardData, isLoadingLeaderboard);

  const [expanded, setExpanded] = useState({ gainers: false, losers: false, incoming: false });
  const toggle = (key: keyof typeof expanded) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const [incomingQuery, setIncomingQuery] = useState("");

  const gainerRank = useMemo(
    () =>
      new Map(
        pricedGainers
          .slice(0, expanded.gainers ? LEADERBOARD_DISPLAY_COUNT : COLLAPSED_ROWS)
          .map((a, i) => [a.id, i + 1])
      ),
    [pricedGainers, expanded.gainers]
  );

  const loserRank = useMemo(
    () =>
      new Map(
        pricedLosers
          .slice(0, expanded.losers ? LEADERBOARD_DISPLAY_COUNT : COLLAPSED_ROWS)
          .map((a, i) => [a.id, i + 1])
      ),
    [pricedLosers, expanded.losers]
  );

  const filteredIncoming = useMemo(() => {
    const q = incomingQuery.trim().toLowerCase();
    const rows = q
      ? incomingListings.filter(
          (a) =>
            a.currency.toLowerCase().includes(q) ||
            (a.tokenMetadata?.name ?? "").toLowerCase().includes(q) ||
            a.exchange.toLowerCase().includes(q)
        )
      : incomingListings;
    return expanded.incoming ? rows : rows.slice(0, COLLAPSED_ROWS);
  }, [incomingListings, incomingQuery, expanded.incoming]);

  // Analysis view data
  const {
    data: tokenMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useTokenMetadata(baseSymbol, isAnalysisView);

  const { data: previousAlerts = [], isLoading: isLoadingAlerts } =
    usePreviousAlerts(userId, baseSymbol, "", isAnalysisView);

  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);

  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, isAnalysisView);

  const globallyAvailableExchangeCodes = useMemo(() => {
    const allExchanges = tokenAvailability?.userExchanges ?? [];
    return allExchanges.filter(
      (code) => !code.endsWith("-demo") && !code.endsWith("-testnet")
    );
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );

  const supportedUserExchangeCodes = useMemo(() => {
    return exchanges
      .filter((e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet"))
      .map((e) => e.code)
      .filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, globallyAvailableSet]);

  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const { price: livePrice } = useTokenLivePrice(baseSymbol, "dynamic", isAnalysisView);
  const { data: market } = useTokenMarket(baseSymbol, isAnalysisView);
  const tags = metadataTags(tokenMetadata);
  const shownPrice = livePrice ?? market?.price ?? null;

  const handleOpenTrade = useCallback(() => setIsTradeDrawerOpen(true), []);

  const handleAnalyze = useCallback(() => {
    if (!inputSymbol.trim()) return;
    setIsAnalyzing(true);
    handleAnalyzeSymbol(inputSymbol);
    setTimeout(() => setIsAnalyzing(false), 500);
  }, [inputSymbol, handleAnalyzeSymbol]);


  const leaderboardTable = (
    kind: "gainers" | "losers",
    rows: typeof sortedGainers,
    rank: Map<string, number>
  ) => (
    <>
      <TableHead
        grid={LEADERBOARD_ROW_GRID}
        cells={[{ label: "#" }, { label: "Token" }, { label: "Price" }, { label: "Alert" }, { label: "Change", align: "right" }]}
      />
      {(isLoadingLeaderboard || isPriceLoading) && (
        <div className="flex justify-center py-8">
          <Loader2 className="size-8 animate-spin text-accent" />
        </div>
      )}
      {!isLoadingLeaderboard && !isPriceLoading && rank.size === 0 && (
        <p className="px-4 py-6 text-sm text-[#a3a3a3]">No listings with price data</p>
      )}
      {/* Rows stay mounted so their live price subscriptions keep running;
          the ones outside the top list are hidden. */}
      {!isLoadingLeaderboard &&
        [...rows]
          .sort((a, b) => (rank.get(a.id) ?? 99) - (rank.get(b.id) ?? 99))
          .map((alert) => (
            <div
              key={`${kind}-${alert.id}`}
              className={isPriceLoading || !rank.has(alert.id) ? "hidden" : "[&:last-child>div]:border-b-0"}
            >
              <LeaderboardTokenTile
                variant="row"
                rank={rank.get(alert.id)}
                alert={alert}
                onPriceUpdate={handlePriceUpdate}
                onAnalyze={handleAnalyzeSymbol}
              />
            </div>
          ))}
    </>
  );

  const linkChips = tokenMetadata?.urls
    ? ([
        ["Website", tokenMetadata.urls.website?.[0]],
        ["X / Twitter", tokenMetadata.urls.twitter?.[0]],
        ["Whitepaper", tokenMetadata.urls.technical_doc?.[0]],
        ["Explorer", tokenMetadata.urls.explorer?.[0]],
        ["GitHub", tokenMetadata.urls.source_code?.[0]],
      ].filter(([, href]) => !!href) as Array<[string, string]>)
    : [];

  return (
    <DesktopLayout
      pageTitle="Analyze"
      hideTitle
      showRightSidebar={false}
    >
      {!isAnalysisView && (
        <div className="flex flex-col gap-8 pb-8">
          <AnalyzeHero
            value={inputSymbol}
            onChange={setInputSymbol}
            onSubmit={handleAnalyze}
            onPick={handleAnalyzeSymbol}
            busy={isAnalyzing}
          />

          <div className="grid gap-6 xl:grid-cols-2">
            <TableCard
              tone="green"
              icon={<TrendingUp />}
              title="Top Gainers"
              subtitle="Recent listings with the highest price increase"
              actions={<ViewAllButton expanded={expanded.gainers} onClick={() => toggle("gainers")} />}
            >
              {leaderboardTable("gainers", sortedGainers, gainerRank)}
            </TableCard>
            <TableCard
              tone="red"
              icon={<TrendingDown />}
              title="Top Losers"
              subtitle="Recent delistings with the largest price drop"
              actions={<ViewAllButton expanded={expanded.losers} onClick={() => toggle("losers")} />}
            >
              {leaderboardTable("losers", sortedLosers, loserRank)}
            </TableCard>
          </div>

          <TableCard
            tone="amber"
            icon={<Clock />}
            title="Incoming Listings"
            subtitle="Tokens listed on pre-market, roadmap, or coming soon"
            actions={
              <div className="flex items-center gap-3">
                <label className="flex h-10 w-[240px] items-center gap-2 rounded-lg border border-white/10 bg-[#0d121a]/80 px-3">
                  <Search className="size-4 text-[#a3a3a3]" />
                  <input
                    value={incomingQuery}
                    onChange={(e) => setIncomingQuery(e.target.value)}
                    placeholder="Search upcoming listings..."
                    className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-[#a3a3a3] focus:outline-none"
                  />
                </label>
                <ViewAllButton expanded={expanded.incoming} onClick={() => toggle("incoming")} />
              </div>
            }
            head={
              <TableHead
                grid={INCOMING_ROW_GRID}
                cells={[
                  { label: "#" },
                  { label: "Token" },
                  { label: "Exchange" },
                  { label: "Source" },
                  { label: "Announced" },
                  { label: "Status", align: "right" },
                ]}
              />
            }
          >
            {isLoadingIncoming ? (
              <div className="flex justify-center py-8">
                <Loader2 className="size-8 animate-spin text-accent" />
              </div>
            ) : filteredIncoming.length === 0 ? (
              <p className="px-4 py-6 text-sm text-[#a3a3a3]">
                {incomingQuery ? "No upcoming listing matches your search" : "No incoming listings at the moment"}
              </p>
            ) : (
              filteredIncoming.map((alert, i) => (
                <IncomingListingTile
                  key={alert.id}
                  variant="row"
                  rank={i + 1}
                  alert={alert}
                  onAnalyze={handleAnalyzeSymbol}
                />
              ))
            )}
          </TableCard>
        </div>
      )}

      {isAnalysisView && (
        <div className="grid items-start gap-6 pb-8 xl:grid-cols-[minmax(0,1fr)_400px]">
          <div className="flex min-w-0 flex-col gap-6">
            <nav className="flex items-center gap-2 text-sm font-medium">
              <button
                type="button"
                onClick={handleBackToDashboard}
                className="flex items-center gap-1 text-[#a3a3a3] transition-colors hover:text-white"
              >
                <ChevronLeft className="size-4" />
                Analyze
              </button>
              <ChevronRight className="size-4 text-[#525252]" />
              <span className="text-white">{baseSymbol}</span>
            </nav>

            <div className="flex items-center gap-5">
              <TokenIcon symbol={baseSymbol} logo={tokenMetadata?.logo} size="lg" />
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex items-baseline gap-3">
                  <h2 className="text-4xl font-semibold text-white">{baseSymbol}</h2>
                  {tokenMetadata?.name && <span className="text-2xl text-[#a3a3a3]">{tokenMetadata.name}</span>}
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-[#6941c6] bg-[#2c1c5f] px-2 py-0.5 text-xs font-medium text-[#d6bbfb]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {(isLoadingMetadata || isLoadingAlerts || isLoadingAvailability || isLoadingExchanges) && (
              <div className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin text-accent" />
                <span className="text-sm text-[#a3a3a3]">Loading token data...</span>
              </div>
            )}
            {metadataError && !isLoadingMetadata && (
              <p className="text-sm text-[#fdb022]">Could not load complete token information.</p>
            )}

            {tokenMetadata?.description && (
              <p className="text-base leading-6 text-[#d4d4d4]">{tokenMetadata.description}</p>
            )}

            {linkChips.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {linkChips.map(([label, href]) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-md border border-[#2dcc89]/50 bg-[#07a66c]/10 px-2.5 py-1 text-sm font-medium text-[#2dcc89] transition-colors hover:bg-[#07a66c]/20"
                  >
                    <Globe className="size-3.5" />
                    {label}
                  </a>
                ))}
              </div>
            )}

            <section className="glass flex flex-col gap-4 !rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <TokenIcon symbol={baseSymbol} logo={tokenMetadata?.logo} size="md" />
                <div className="flex-1">
                  <p className="text-base font-semibold text-white">{baseSymbol} / USDT</p>
                  {tokenMetadata?.name && <p className="text-xs text-[#a3a3a3]">{tokenMetadata.name}</p>}
                </div>
                {livePrice != null && (
                  <span className="text-base font-semibold tabular-nums text-white">
                    ${livePrice.toLocaleString("en-US", { maximumFractionDigits: 8 })}
                  </span>
                )}
              </div>
              <TradingViewChartWidget key={`chart-${normalizedSymbol}`} symbol={normalizedSymbol} />
            </section>

            <AlertHistory alerts={previousAlerts} variant="page-desktop" baseSymbol={baseSymbol} />
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-base text-[#d4d4d4]">{baseSymbol} Price</p>
              <p className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold tabular-nums text-white">
                  {shownPrice != null ? `$${shownPrice.toLocaleString("en-US", { maximumFractionDigits: 8 })}` : "–"}
                </span>
                {market?.percentChange24h != null && (
                  <span className={`text-lg font-medium ${market.percentChange24h >= 0 ? "text-[#2dcc89]" : "text-[#f97066]"}`}>
                    {market.percentChange24h >= 0 ? "+" : ""}
                    {market.percentChange24h.toFixed(2)}% (24h)
                  </span>
                )}
              </p>
            </div>

            {market && <TokenMarketStats market={market} symbol={baseSymbol} />}

            {hasTradableExchange && (
              <button
                type="button"
                onClick={handleOpenTrade}
                className="flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-white/[0.12] bg-[#07a66c] font-semibold text-white transition-colors hover:bg-[#07a66c]/85"
              >
                <ArrowUpRight className="size-5" />
                Trade
              </button>
            )}

            {globallyAvailableExchangeCodes.length > 0 && (
              <section className="glass flex flex-col gap-4 !rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <Hexagon className="size-6 text-[#2dcc89]" />
                  <p className="text-base font-semibold text-white">Exchange Availability</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {globallyAvailableExchangeCodes.map((code) => {
                    const connected = supportedUserExchangeCodes.includes(code);
                    return (
                      <span
                        key={code}
                        className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm font-medium text-white ${
                          connected ? "border-[#2dcc89]/60 bg-[#07a66c]/10" : "border-white/10 bg-white/[0.03]"
                        }`}
                        title={connected ? "Connected" : "Not connected"}
                      >
                        <ExchangeLogo code={code} size={16} />
                        {exchangeBrand(code).name}
                      </span>
                    );
                  })}
                </div>
                {hasTradableExchange && (
                  <p className="text-xs text-[#2dcc89]">Green ones are connected, so you can trade it there.</p>
                )}
              </section>
            )}

            <section className="glass flex flex-col gap-3 !rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <Brain className="size-6 text-[#2dcc89]" />
                <p className="text-base font-semibold text-white">AI-Powered Analysis</p>
              </div>
              <p className="text-xs text-[#a3a3a3]">Comprehensive analysis and insight about {baseSymbol}</p>
              <TokenAnalysisSection tokenSymbol={baseSymbol} />
            </section>
          </div>
        </div>
      )}

      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={normalizedSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => setIsTradeDrawerOpen(open)}
        onGoToPosition={() => {}}
      />
    </DesktopLayout>
  );
};
